import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Save,
  PlugZap,
  CheckCircle2,
  XCircle,
  KeyRound,
  Copy,
  ExternalLink,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { LLM_MODEL_IDS, LLM_MODEL_LABELS } from '@fenix/shared';
import type { Settings, SettingsInput } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { useCan } from '@/features/auth/store';
import { WebhookEventsCard } from './WebhookEventsCard';
import { SourceSyncTab } from './SourceSyncTab';
import { ConcurrencyHint } from './ConcurrencyHint';
import { ProviderAgentsList } from './ProviderAgentsList';
import { AutopilotCard } from './AutopilotCard';
import { EmailTemplateCard } from './EmailTemplateCard';

const TIMEZONES = [
  'America/Sao_Paulo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'Europe/London',
  'Europe/Lisbon',
  'UTC',
];

export function SettingsPage() {
  const qc = useQueryClient();
  const isAdmin = useCan('settings.manage');
  const q = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });
  const languages = useQuery({ queryKey: ['languages'], queryFn: settingsApi.languages });
  const [form, setForm] = useState<SettingsInput>({});
  const [retellKey, setRetellKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [twilioSecret, setTwilioSecret] = useState('');
  const [sourcePassword, setSourcePassword] = useState('');
  const [resendKey, setResendKey] = useState('');
  const [resendWebhookSecret, setResendWebhookSecret] = useState('');
  const [emailTestTo, setEmailTestTo] = useState('');
  const [test, setTest] = useState<Awaited<ReturnType<typeof settingsApi.testConnection>> | null>(
    null,
  );

  useEffect(() => {
    if (q.data) {
      const {
        hasRetellApiKey: _a,
        hasOpenaiApiKey: _b,
        hasTwilioApiKeySecret: _f,
        hasSourceDbPassword: _g,
        hasResendApiKey: _h,
        hasResendWebhookSecret: _i,
        retellWebhookConfigured: _d,
        webhookBaseUrl: _e,
        ...rest
      } = q.data;
      setForm(rest);
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({
        ...form,
        ...(retellKey ? { retellApiKey: retellKey } : {}),
        ...(openaiKey ? { openaiApiKey: openaiKey } : {}),
        ...(twilioSecret ? { twilioApiKeySecret: twilioSecret } : {}),
        ...(sourcePassword ? { sourceDbPassword: sourcePassword } : {}),
        ...(resendKey ? { resendApiKey: resendKey } : {}),
        ...(resendWebhookSecret ? { resendWebhookSecret } : {}),
      }),
    onSuccess: () => {
      toast.success('Configurações salvas');
      setRetellKey('');
      setOpenaiKey('');
      setTwilioSecret('');
      setSourcePassword('');
      setResendKey('');
      setResendWebhookSecret('');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });
  const testMut = useMutation({
    mutationFn: settingsApi.testConnection,
    onSuccess: (r) => {
      setTest(r);
      (r.ok ? toast.success : toast.error)(r.message);
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha no teste'),
  });
  const emailTest = useMutation({
    mutationFn: () => settingsApi.emailTest(emailTestTo.trim()),
    onSuccess: (r) => toast.success(r.message),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha no envio de teste'),
  });
  const clearKey = useMutation({
    mutationFn: settingsApi.clearKey,
    onSuccess: () => {
      toast.success('Chave removida');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  if (q.isLoading || !q.data) return <Skeleton className="h-96 w-full" />;
  const s: Settings = q.data;
  const set = <K extends keyof SettingsInput>(k: K, v: SettingsInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <PageHeader
        title="Configurações"
        description="Provedor de voz, idioma das ligações, horários e limites do discador"
        actions={
          isAdmin && (
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Salvar
            </Button>
          )
        }
      />

      <Tabs defaultValue="provider">
        <TabsList>
          <TabsTrigger value="provider">Provedor de voz</TabsTrigger>
          <TabsTrigger value="general">Geral e idioma</TabsTrigger>
          <TabsTrigger value="sms">SMS</TabsTrigger>
          <TabsTrigger value="email">E-mail</TabsTrigger>
          <TabsTrigger value="integration">Integração</TabsTrigger>
          <TabsTrigger value="dialer">Discador</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="provider" className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Provedor</CardTitle>
              <CardDescription>
                Quem faz as ligações. Use o simulador enquanto não tiver conta no Retell.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="space-y-1.5">
                <Label>Provedor ativo</Label>
                <Select
                  value={form.voiceProvider ?? s.voiceProvider}
                  onValueChange={(v) => set('voiceProvider', v as never)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retell">Retell AI (ligações reais)</SelectItem>
                    <SelectItem value="mock">Simulador (sem ligações)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Modelo de linguagem (LLM)</Label>
                <Select
                  value={form.llmModel ?? s.llmModel}
                  onValueChange={(v) => set('llmModel', v as never)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LLM_MODEL_IDS.map((id) => (
                      <SelectItem key={id} value={id}>
                        {LLM_MODEL_LABELS[id]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Vale para todos os agentes, inclusive os de marca com base de conhecimento; cada
                  um é atualizado na próxima ligação. O custo por minuto muda conforme o modelo
                  (tabela de preços do Retell).
                </p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>API key do Retell</Label>
                  {s.hasRetellApiKey ? (
                    <Badge variant="secondary" className="gap-1">
                      <CheckCircle2 className="size-3 text-success" /> configurada
                    </Badge>
                  ) : (
                    <Badge variant="outline">não configurada</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder={s.hasRetellApiKey ? '•••••••• (cole para substituir)' : 'key_…'}
                    value={retellKey}
                    onChange={(e) => setRetellKey(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {s.hasRetellApiKey && isAdmin && (
                    <Button
                      variant="outline"
                      size="icon"
                      title="Remover chave"
                      onClick={() => clearKey.mutate('retell')}
                    >
                      <KeyRound />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Obtenha em{' '}
                  <a
                    className="inline-flex items-center gap-0.5 underline"
                    href="https://dashboard.retellai.com/apiKey"
                    target="_blank"
                    rel="noreferrer"
                  >
                    dashboard.retellai.com → API Keys <ExternalLink className="size-3" />
                  </a>
                  . A chave é criptografada no banco.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Número de origem (comprado no Retell)</Label>
                <Input
                  placeholder="+1XXXXXXXXXX"
                  value={form.fromNumber ?? ''}
                  onChange={(e) => set('fromNumber', e.target.value || null)}
                  disabled={!isAdmin}
                />
                {test?.phoneNumbers.length ? (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {test.phoneNumbers.map((n) => (
                      <Button
                        key={n.number}
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => set('fromNumber', n.number)}
                      >
                        {n.number}
                        {n.nickname ? ` · ${n.nickname}` : ''}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>API key da OpenAI (opcional)</Label>
                  {s.hasOpenaiApiKey ? (
                    <Badge variant="secondary">configurada</Badge>
                  ) : (
                    <Badge variant="outline">opcional</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="sk-…"
                    value={openaiKey}
                    onChange={(e) => setOpenaiKey(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {s.hasOpenaiApiKey && isAdmin && (
                    <Button variant="outline" size="icon" onClick={() => clearKey.mutate('openai')}>
                      <KeyRound />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  O Retell já inclui o modelo de linguagem; esta chave fica reservada para recursos
                  futuros (resumos, análises próprias).
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Testar conexão</CardTitle>
              <CardDescription>
                Valida a chave, lista os números disponíveis e confirma a URL do webhook.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="outline"
                onClick={() => testMut.mutate()}
                disabled={testMut.isPending || !isAdmin}
              >
                {testMut.isPending ? <Loader2 className="animate-spin" /> : <PlugZap />} Testar
                conexão
              </Button>
              <p className="text-xs text-muted-foreground">
                Salve as configurações antes de testar se alterou a chave.
              </p>
              {test && (
                <Alert variant={test.ok ? 'default' : 'destructive'}>
                  {test.ok ? <CheckCircle2 className="text-success" /> : <XCircle />}
                  <AlertTitle>{test.ok ? 'Conectado' : 'Falha'}</AlertTitle>
                  <AlertDescription className="space-y-1">
                    <p>{test.message}</p>
                    <p className="text-xs">
                      {test.agents
                        ? `${test.agents} agente(s) criado(s) por este ambiente`
                        : 'Nenhum agente criado ainda — nasce na primeira ligação'}
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              <div className="space-y-1.5">
                <Label>URL do webhook</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={
                      test?.webhookUrl ??
                      `${s.webhookBaseUrl}/api/webhooks/${form.voiceProvider ?? s.voiceProvider}`
                    }
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      void navigator.clipboard.writeText(
                        test?.webhookUrl ?? `${s.webhookBaseUrl}/api/webhooks/${s.voiceProvider}`,
                      );
                      toast.success('Copiado');
                    }}
                  >
                    <Copy />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  O sistema configura o webhook no agente automaticamente ao fazer a primeira
                  ligação. Em desenvolvimento, a API precisa estar exposta publicamente (ngrok) e{' '}
                  <code>PUBLIC_BASE_URL</code> no <code>.env</code> deve apontar para essa URL.
                </p>
              </div>
              <ProviderAgentsList />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Identidade e idioma</CardTitle>
              <CardDescription>
                O idioma define o script padrão, a voz e o reconhecimento de fala.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="companyName">Nome da empresa</Label>
                <Input
                  id="companyName"
                  value={form.companyName ?? ''}
                  onChange={(e) => set('companyName', e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agentName">Nome da atendente (IA)</Label>
                <Input
                  id="agentName"
                  value={form.agentName ?? ''}
                  onChange={(e) => set('agentName', e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Idioma padrão das ligações</Label>
                <Select
                  value={form.callLanguage ?? s.callLanguage}
                  onValueChange={(v) => set('callLanguage', v as never)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.data?.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fuso horário</Label>
                <Select
                  value={form.timezone ?? s.timezone}
                  onValueChange={(v) => set('timezone', v)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Oferta padrão (usada quando o produto não tem oferta própria)</Label>
                <Textarea
                  rows={2}
                  value={form.defaultOfferText ?? ''}
                  onChange={(e) => set('defaultOfferText', e.target.value || null)}
                  disabled={!isAdmin}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sms" className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Twilio</CardTitle>
              <CardDescription>
                O link de checkout é enviado pela sua conta Twilio, não pelo Retell — por isso
                funciona igual na ligação pelo navegador e no telefone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Enviar link por SMS</Label>
                  <p className="text-xs text-muted-foreground">
                    A IA só recebe a ferramenta quando isto está ligado e as credenciais abaixo
                    estão completas.
                  </p>
                </div>
                <Switch
                  checked={form.smsEnabled ?? s.smsEnabled}
                  onCheckedChange={(v) => set('smsEnabled', v)}
                  disabled={!isAdmin}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Account SID da subconta</Label>
                <Input
                  placeholder="AC…"
                  value={form.twilioAccountSid ?? ''}
                  onChange={(e) => set('twilioAccountSid', e.target.value || null)}
                  disabled={!isAdmin}
                />
              </div>

              <div className="space-y-1.5">
                <Label>API Key SID</Label>
                <Input
                  placeholder="SK…"
                  value={form.twilioApiKeySid ?? ''}
                  onChange={(e) => set('twilioApiKeySid', e.target.value || null)}
                  disabled={!isAdmin}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>API Key Secret</Label>
                  {s.hasTwilioApiKeySecret ? (
                    <Badge variant="secondary">configurado</Badge>
                  ) : (
                    <Badge variant="outline">pendente</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="só aparece uma vez na Twilio"
                    value={twilioSecret}
                    onChange={(e) => setTwilioSecret(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {s.hasTwilioApiKeySecret && isAdmin && (
                    <Button variant="outline" size="icon" onClick={() => clearKey.mutate('twilio')}>
                      <KeyRound />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Criptografado no banco. A chave é escopada e pode ser revogada sem derrubar a
                  conta.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Messaging Service SID</Label>
                <Input
                  placeholder="MG…"
                  value={form.twilioMessagingServiceSid ?? ''}
                  onChange={(e) => set('twilioMessagingServiceSid', e.target.value || null)}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  O envio vai pelo Messaging Service, não pelo número direto: é nele que a campanha
                  A2P 10DLC fica registrada.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Cópias dos SMS de teste</Label>
                <Textarea
                  rows={3}
                  placeholder="+19374428575 → +5588997297874"
                  value={form.smsTestCopies ?? ''}
                  onChange={(e) => set('smsTestCopies', e.target.value || null)}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  Um par por linha: <span className="font-medium">número de teste → celular</span>.
                  Quando o SMS de uma ligação vai para um número da lista, uma cópia sai pelo mesmo
                  Messaging Service para o celular — é como quem testa com um número americano que
                  só encaminha a ligação recebe o link. Cliente real nunca entra aqui.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Antes do primeiro envio</CardTitle>
              <CardDescription>Dois ajustes que só existem no console da Twilio.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="space-y-1">
                <p className="font-medium">Permissão de país</p>
                <p className="text-muted-foreground">
                  Por padrão a Twilio só entrega no país de origem da conta. Para receber no seu
                  celular durante os testes, habilite o Brasil em{' '}
                  <span className="font-medium">
                    Console → Messaging → Settings → Geo permissions
                  </span>
                  , dentro da subconta. Sem isso o envio falha com o erro 21408.
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-medium">Registro A2P 10DLC</p>
                <p className="text-muted-foreground">
                  Necessário só para entregar em celulares americanos. As operadoras bloqueiam
                  tráfego não registrado. O envio internacional para o Brasil não depende dele.
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-medium">Status de entrega</p>
                <p className="text-muted-foreground">
                  Cada mensagem enviada aparece na tela da chamada com o status vindo da Twilio, e o
                  link exato fica registrado para conferência.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email" className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resend</CardTitle>
              <CardDescription>
                Segunda via do link de checkout. Sai sozinha quando o SMS não é entregue ou quando a
                IA combina e-mail na ligação; também dá para enviar pelo botão na chamada.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Enviar link por e-mail</Label>
                  <p className="text-xs text-muted-foreground">
                    Só funciona com a chave e o remetente abaixo preenchidos. A IA passa a dizer que
                    o e-mail sai automaticamente.
                  </p>
                </div>
                <Switch
                  checked={form.emailEnabled ?? s.emailEnabled}
                  onCheckedChange={(v) => set('emailEnabled', v)}
                  disabled={!isAdmin}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>API key do Resend</Label>
                  {s.hasResendApiKey ? (
                    <Badge variant="secondary">configurada</Badge>
                  ) : (
                    <Badge variant="outline">pendente</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder={s.hasResendApiKey ? '•••••••• (cole para substituir)' : 're_…'}
                    value={resendKey}
                    onChange={(e) => setResendKey(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {s.hasResendApiKey && isAdmin && (
                    <Button variant="outline" size="icon" onClick={() => clearKey.mutate('resend')}>
                      <KeyRound />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Criptografada no banco.</p>
              </div>

              <div className="space-y-1.5">
                <Label>Remetente (e-mail)</Label>
                <Input
                  type="email"
                  placeholder="orders@groupphoenixmediabuyer.com"
                  value={form.emailFromAddress ?? ''}
                  onChange={(e) => set('emailFromAddress', e.target.value || null)}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  Precisa ser de um domínio verificado no Resend (registros DKIM e SPF no DNS). O
                  nome que aparece é o da marca do produto, a menos que você fixe um abaixo.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Nome do remetente (opcional)</Label>
                  <Input
                    placeholder="vazio = nome da marca"
                    value={form.emailFromName ?? ''}
                    onChange={(e) => set('emailFromName', e.target.value || null)}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Responder para (opcional)</Label>
                  <Input
                    type="email"
                    placeholder="support@groupphoenixmediabuyer.com"
                    value={form.emailReplyTo ?? ''}
                    onChange={(e) => set('emailReplyTo', e.target.value || null)}
                    disabled={!isAdmin}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Signing secret do webhook</Label>
                  {s.hasResendWebhookSecret ? (
                    <Badge variant="secondary">configurado</Badge>
                  ) : (
                    <Badge variant="outline">pendente</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="whsec_…"
                    value={resendWebhookSecret}
                    onChange={(e) => setResendWebhookSecret(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {s.hasResendWebhookSecret && isAdmin && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => clearKey.mutate('resendWebhook')}
                    >
                      <KeyRound />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  É o que traz entregue, aberto, clicado e devolvido para a tela da chamada. No
                  Resend, cadastre o webhook em{' '}
                  <span className="font-mono">{s.webhookBaseUrl}/api/webhooks/resend</span> e cole
                  aqui o secret que ele mostra.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Testar envio</CardTitle>
              <CardDescription>
                Salve a configuração primeiro. O teste prova chave, remetente e domínio verificado.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={emailTestTo}
                  onChange={(e) => setEmailTestTo(e.target.value)}
                  disabled={!isAdmin}
                />
                <Button
                  variant="outline"
                  onClick={() => emailTest.mutate()}
                  disabled={!isAdmin || !emailTestTo.trim() || emailTest.isPending}
                >
                  {emailTest.isPending ? <Loader2 className="animate-spin" /> : <Mail />} Enviar
                  teste
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                O e-mail do cliente usa o idioma da ligação, leva o link de checkout, o cupom e a
                oferta do produto, e se apresenta como a marca. Quando a IA confirma um e-mail na
                ligação, ele é guardado no lead e usado no envio.
              </p>
            </CardContent>
          </Card>

          <EmailTemplateCard isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="integration" className="grid gap-4 lg:grid-cols-2">
          <SourceSyncTab
            form={form}
            set={set}
            settings={s}
            isAdmin={isAdmin}
            password={sourcePassword}
            setPassword={setSourcePassword}
            onClearPassword={() => clearKey.mutate('source')}
          />
        </TabsContent>

        <TabsContent value="dialer" className="grid items-start gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Regras do discador</CardTitle>
              <CardDescription>
                Aplicadas às campanhas. "Ligar agora" ignora a janela de horário.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Início da janela</Label>
                <Input
                  type="time"
                  value={form.callWindowStart ?? ''}
                  onChange={(e) => set('callWindowStart', e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Fim da janela</Label>
                <Input
                  type="time"
                  value={form.callWindowEnd ?? ''}
                  onChange={(e) => set('callWindowEnd', e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Máx. tentativas por lead</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={form.maxAttempts ?? ''}
                  onChange={(e) => set('maxAttempts', Number(e.target.value))}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Intervalo entre tentativas (min)</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.retryIntervalMin ?? ''}
                  onChange={(e) => set('retryIntervalMin', Number(e.target.value))}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Chamadas simultâneas</Label>
                <Input
                  type="number"
                  min={1}
                  max={500}
                  value={form.maxConcurrentCalls ?? ''}
                  onChange={(e) => set('maxConcurrentCalls', Number(e.target.value))}
                  disabled={!isAdmin}
                />
                <ConcurrencyHint value={form.maxConcurrentCalls ?? s.maxConcurrentCalls} />
              </div>
              <div className="space-y-1.5">
                <Label>Chamadas por minuto</Label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={form.callsPerMinute ?? ''}
                  onChange={(e) => set('callsPerMinute', Number(e.target.value))}
                  disabled={!isAdmin}
                />
              </div>
            </CardContent>
          </Card>
          <AutopilotCard form={form} set={set} settings={s} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="webhooks">
          <WebhookEventsCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
